import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { navigationGuards } from './navigationGuard';
import axios from 'axios';

// Import route modules
import adminRoutes from './admin-routes';
import clientRoutes from './client-routes';
import agencyRoutes from './agency-routes';
import publicRoutes from './public-routes';

// Import API constants
const API_KEY = process.env.VUE_APP_API_KEY;

// Helper function to validate company data
const validateCompanyData = async (identifier, type) => {
  try {
    const apiUrl = process.env.VUE_APP_API_URL;
    
    if (!apiUrl) {
      console.error('API URL não está definida');
      return true;
    }

    const response = await axios.get(`${apiUrl}/${type}/${identifier}`, {
      headers: {
        'X-Increment-Counter': 'false',
        'Accept': 'application/json',
        'X-API-Key': API_KEY
      },
      validateStatus: function (status) {
        return status >= 200 && status < 500;
      }
    });
    
    return response.status === 200 && !!response.data?.company_id;
  } catch (error) {
    console.error('Erro na validação:', error);
    return true;
  }
};

// Combine all routes
const routes = [
  ...publicRoutes,
  ...adminRoutes,
  ...clientRoutes,
  ...agencyRoutes  // Adicionando as rotas de agência
];

// Create router instance
const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Global navigation guard
router.beforeEach(async (to, from, next) => {
  console.log('Carregando arquivo router/index.js - ' + new Date().toISOString());
  console.log('Route guard:', to.path, to.name, to.matched);
  
  const authStore = useAuthStore();

  // 1. Always ensure user data is updated
  if (authStore.isAuthenticated) {
    await authStore.checkAuth();
  }

  // 2. Login redirect (when already authenticated)
  if (to.name === 'login' && authStore.isAuthenticated) {
    console.log("Autenticado....>>>>");
    if (authStore.isAdmin) {
      next('/admin/companies');
    } else if (authStore.user?.userType === 'agency') {
      next('/agency/managed-companies');
    } else if (authStore.user?.companyId) {
      next({
        name: 'company-statistics',
        params: { companyId: authStore.user.companyId }
      });
    } else {
      next('/not-found');
    }
    return;
  }

  // 3. Authentication check for protected routes
  if (to.matched.some(record => record.meta.requiresAuth)) {
    const isAuthenticated = await authStore.checkAuth();
    
    if (!isAuthenticated) {
      next({
        name: 'login',
        query: { redirect: to.fullPath }
      });
      return;
    }

    // 4. Admin permission check
    if (to.meta.requiresAdmin && !authStore.isAdmin) {
      // Se o usuário não é admin, redireciona para o dashboard adequado
      if (authStore.user?.userType === 'agency') {
        next('/agency/managed-companies');
      } else if (authStore.user?.companyId) {
        next('/clients/dashboard');
      } else {
        next({ name: 'not-found' });
      }
      return;
    }
    
    // 5. Agency permission check
    console.log("authStore.user?.userType 2", authStore.user?.userType);

    if (to.meta.requiresAgency && authStore.user?.userType !== 'agency') {
      // Se o usuário não é agência, redireciona para o dashboard adequado
      if (authStore.isAdmin) {
        next('/admin/dashboard');
      } else if (authStore.user?.companyId) {
        next('/clients/dashboard');
      } else {
        console.log ("Agency not-found rota index.js")
        next({ name: 'not-found' });
      }
      return;
    }
  }

  // 6. Special handling for NPS form routes
  if (to.name === 'nps-form-slug' || to.name === 'nps-form-custom-link') {
    const identifier = to.params.slug || to.params.custom_link;
    const type = to.name === 'nps-form-slug' ? 's' : 'c';
    
    if (!navigationGuards.validateRouteParams(to)) {
      next({ name: 'not-found' });
      return;
    }

    const isValid = await validateCompanyData(identifier, type);
    if (!isValid) {
      next({ name: 'not-found' });
      return;
    }
  }

  // 7. General navigation validation
  const isValidNavigation = navigationGuards.validateNavigation(to, from);
  const hasValidParams = navigationGuards.validateRouteParams(to);

  if (!isValidNavigation || !hasValidParams) {
    navigationGuards.cleanupNavigation(to);
    next({ name: 'not-found' });
    return;
  }

  // 8. Cleanup temporary data
  navigationGuards.cleanupNavigation(to);

  next();
});

// Handle navigation errors
router.onError((error) => {
  console.error('Navigation error:', error);
  router.push({ name: 'not-found' });
});

export default router;